import { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button } from '@mui/material';
import { useListeningTimeReport } from '../../hooks/useListeningTime';
import { useTopReviewedSegments, useVocabularyDensity, useYouTubeEngagementSummary } from '../../hooks/useLearningMetrics';
import { getSegmentPlayerPath, timeRangeToDays } from '../../services/learningMetrics.service';
import { useLexicalStats, useLexicalBackfill, useLexicalLanguages } from '../../hooks/useLexical';
import { format, subDays, subMonths, subYears } from 'date-fns';
import { Assessment as AssessmentIcon, Repeat as RepeatIcon, MenuBook as MenuBookIcon, AutoStories as AutoStoriesIcon, OndemandVideo as OndemandVideoIcon } from '@mui/icons-material';

type TimeRange = '1w' | '1m' | '6m' | '1y';

// Generate distinct colors for each language
const COLORS = [
  '#8884d8', // Blue
  '#82ca9d', // Green
  '#ffc658', // Yellow
  '#ff7c7c', // Red
  '#a78bfa', // Purple
  '#fb923c', // Orange
  '#60a5fa', // Light Blue
  '#34d399', // Teal
  '#f472b6', // Pink
  '#facc15', // Amber
];

// Colors for exposure tier buckets: single → low → building → strong → phrases → not-mapped
const BUCKET_COLORS = ['#ffc658', '#8884d8', '#82ca9d', '#34d399', '#a78bfa', '#94a3b8'];

export function Reports() {
  const { t } = useTranslation(['reports', 'common']);
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<TimeRange>('1m');

  // Language filter for lexical frequency — seeded from the same key used by VocabularyList
  const LEXICAL_LANG_KEY = 'vocabulary-selected-language';
  const [selectedLexicalLanguage, setSelectedLexicalLanguage] = useState<string>(
    () => localStorage.getItem(LEXICAL_LANG_KEY) || ''
  );

  // Persist selection so it stays in sync with VocabularyList's default
  const handleLexicalLanguageChange = (lang: string) => {
    setSelectedLexicalLanguage(lang);
    if (lang) {
      localStorage.setItem(LEXICAL_LANG_KEY, lang);
    } else {
      localStorage.removeItem(LEXICAL_LANG_KEY);
    }
  };

  // Calculate date range based on selected time range
  const getDateRange = () => {
    const endDate = new Date();
    let startDate: Date;

    switch (timeRange) {
      case '1w':
        startDate = subDays(endDate, 7);
        break;
      case '1m':
        startDate = subMonths(endDate, 1);
        break;
      case '6m':
        startDate = subMonths(endDate, 6);
        break;
      case '1y':
        startDate = subYears(endDate, 1);
        break;
      default:
        startDate = subMonths(endDate, 1);
    }

    return {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
    };
  };

  const dateRange = getDateRange();
  const { data, isLoading, error } = useListeningTimeReport(dateRange);

  // Fetch learning metrics
  const { data: topReviewedData, isLoading: topReviewedLoading } = useTopReviewedSegments({
    limit: 10,
    days: timeRangeToDays(timeRange),
  });
  const { data: vocabDensityData, isLoading: vocabDensityLoading } = useVocabularyDensity({
    limit: 10,
    days: timeRangeToDays(timeRange),
  });
  const { data: ytEngagement, isLoading: ytEngagementLoading } = useYouTubeEngagementSummary();

  // Lexical frequency
  const { data: lexicalLanguages } = useLexicalLanguages();
  const { data: lexicalStats, isLoading: lexicalLoading } = useLexicalStats(
    selectedLexicalLanguage || undefined
  );
  const { mutate: runBackfill, isPending: isBackfilling } = useLexicalBackfill();

  // Exposure distribution chart state + derived data
  const [exposureView, setExposureView] = useState<'count' | 'pct'>('pct');

  const exposureChartData = useMemo(() => {
    if (!lexicalStats?.exposureBuckets) return [];
    const b = lexicalStats.exposureBuckets;
    const total = b.single + b.low + b.building + b.strong;
    if (total === 0) return [];
    return [
      { label: '1', name: t('bucketSingle', 'Single (1)'), count: b.single, pct: Math.round((b.single / total) * 100) },
      { label: '2–5', name: t('bucketLow', 'Low (2–5)'), count: b.low, pct: Math.round((b.low / total) * 100) },
      { label: '6–19', name: t('bucketBuilding', 'Building (6–19)'), count: b.building, pct: Math.round((b.building / total) * 100) },
      { label: '≥20', name: t('bucketStrong', 'Strong (≥20)'), count: b.strong, pct: Math.round((b.strong / total) * 100) },
    ];
  }, [lexicalStats, t]);

  const vocabChartData = useMemo(() => {
    if (!lexicalStats?.vocabDistribution) return [];
    const v = lexicalStats.vocabDistribution;
    return [
      { name: t('bucketSingle', 'Single (1)'), value: v.single, color: BUCKET_COLORS[0] },
      { name: t('bucketLow', 'Low (2–5)'), value: v.low, color: BUCKET_COLORS[1] },
      { name: t('bucketBuilding', 'Building (6–19)'), value: v.building, color: BUCKET_COLORS[2] },
      { name: t('bucketStrong', 'Strong (≥20)'), value: v.strong, color: BUCKET_COLORS[3] },
      { name: t('bucketPhrases', 'Phrases'), value: v.phrases, color: BUCKET_COLORS[4] },
      { name: t('bucketNotMapped', 'Not Mapped'), value: v.notMapped, color: BUCKET_COLORS[5] },
    ].filter(d => d.value > 0);
  }, [lexicalStats, t]);

  const handleTimeRangeChange = (_event: React.MouseEvent<HTMLElement>, newRange: TimeRange | null) => {
    if (newRange !== null) {
      setTimeRange(newRange);
    }
  };

  // Format seconds into hours and minutes (consistent with Dashboard)
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Transform data for the chart
  const chartData = (() => {
    if (!data || data.length === 0) return [];

    // Group by date and create data points with all languages
    const groupedByDate = new Map<string, Record<string, number>>();
    const allLanguages = new Set<string>();

    data.forEach(item => {
      allLanguages.add(item.language);
      if (!groupedByDate.has(item.date)) {
        groupedByDate.set(item.date, {});
      }
      const dateData = groupedByDate.get(item.date)!;
      dateData[item.language] = item.totalSeconds;
    });

    // Helper function to format date string without timezone conversion
    const formatDateString = (dateStr: string) => {
      // dateStr is in format "YYYY-MM-DD"
      const [year, month, day] = dateStr.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return format(date, 'MMM dd');
    };

    // Convert to array format for Recharts
    const chartArray = Array.from(groupedByDate.entries()).map(([date, languageData]) => ({
      date: formatDateString(date),
      fullDate: date,
      ...languageData,
    }));

    // Sort by date
    chartArray.sort((a, b) => a.fullDate.localeCompare(b.fullDate));

    return chartArray;
  })();

  // Get unique languages for generating lines
  const languages = Array.from(
    new Set(data?.map(item => item.language) || [])
  );

  // Calculate total listening time for the period
  const totalSeconds = data?.reduce((sum, item) => sum + item.totalSeconds, 0) || 0;

  // Per-language totals (sorted descending)
  const languageTotals = languages
    .map(lang => ({
      language: lang,
      seconds: data?.filter(item => item.language === lang).reduce((sum, item) => sum + item.totalSeconds, 0) || 0,
    }))
    .sort((a, b) => b.seconds - a.seconds);

  // Custom tooltip to show formatted time
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5, border: '1px solid #ccc' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color }}
            >
              {entry.name}: {formatTime(entry.value)}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AssessmentIcon sx={{ fontSize: 32, mr: 1.5 }} />
        <Typography variant="h4" component="h1">
          {t('title', 'Reports')}
        </Typography>
      </Box>

      {/* Time Range Filter */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={handleTimeRangeChange}
          aria-label={t('timeRange', 'time range')}
        >
          <ToggleButton value="1w" aria-label={t('oneWeek', '1 Week')}>
            {t('oneWeek', '1 Week')}
          </ToggleButton>
          <ToggleButton value="1m" aria-label={t('oneMonth', '1 Month')}>
            {t('oneMonth', '1 Month')}
          </ToggleButton>
          <ToggleButton value="6m" aria-label={t('sixMonths', '6 Months')}>
            {t('sixMonths', '6 Months')}
          </ToggleButton>
          <ToggleButton value="1y" aria-label={t('oneYear', '1 Year')}>
            {t('oneYear', '1 Year')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Summary Card */}
      {!isLoading && !error && data && data.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('totalListeningTime', 'Total Listening Time')}
            </Typography>
            <Typography variant="h3" color="primary">
              {formatTime(totalSeconds)}
            </Typography>
            {languages.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Stack direction="row" flexWrap="wrap" gap={2}>
                  {languageTotals.map((lt, index) => (
                    <Box key={lt.language} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: COLORS[index % COLORS.length],
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {lt.language}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatTime(lt.seconds)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {t('errorLoading', 'Failed to load listening time data. Please try again.')}
        </Alert>
      )}

      {/* Empty State */}
      {!isLoading && !error && data && data.length === 0 && (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            backgroundColor: 'background.default',
          }}
        >
          <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {t('noData', 'No listening data yet')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t(
              'reports.noDataDescription',
              'Start listening to podcasts to see your progress here! Every minute you spend learning will be tracked and visualized in this chart.'
            )}
          </Typography>
        </Paper>
      )}

      {/* Chart */}
      {!isLoading && !error && data && data.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('listeningTimeByDay', 'Listening Time by Day')}
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis
                tickFormatter={(value) => {
                  const hours = Math.floor(value / 3600);
                  const mins = Math.floor((value % 3600) / 60);
                  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
                  if (hours > 0) return `${hours}h`;
                  return `${mins}m`;
                }}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ paddingTop: '20px' }} />
              {languages.map((language, index) => (
                <Line
                  key={language}
                  type="monotone"
                  dataKey={language}
                  name={language}
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Word Exposure Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          {t('wordExposure', 'Word Exposure')}
        </Typography>

        <Grid container spacing={3}>
          {/* Stats + Backfill */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AutoStoriesIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">
                    {t('lexicalFrequency', 'Lexical Frequency')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  {lexicalLanguages && lexicalLanguages.length > 0 && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="lexical-lang-label">
                        {t('language', 'Language')}
                      </InputLabel>
                      <Select
                        labelId="lexical-lang-label"
                        value={selectedLexicalLanguage}
                        label={t('language', 'Language')}
                        onChange={(e) => handleLexicalLanguageChange(e.target.value)}
                      >
                        <MenuItem value="">
                          <em>{t('allLanguages', 'All languages')}</em>
                        </MenuItem>
                        {lexicalLanguages.map((lang) => (
                          <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => runBackfill(undefined)}
                    disabled={isBackfilling}
                    startIcon={isBackfilling ? <CircularProgress size={16} /> : undefined}
                  >
                    {isBackfilling
                      ? t('backfilling', 'Processing...')
                      : t('runBackfill', 'Refresh from Transcripts')}
                  </Button>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('lexicalDescription', 'Words encountered across all your transcripts')}
              </Typography>

              {lexicalLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : lexicalStats ? (
                <Box>
                  {/* Summary chips */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <Chip
                      label={t('uniqueWords', '{{count}} unique words', { count: lexicalStats.totalUniqueWords })}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={t('totalOccurrences', '{{count}} total occurrences', { count: lexicalStats.totalWordOccurrences })}
                      color="secondary"
                      variant="outlined"
                    />
                    {lexicalStats.vocabularyInLexicon > 0 && (
                      <Chip
                        label={t('vocabInLexicon', '{{count}} vocab words tracked', { count: lexicalStats.vocabularyInLexicon })}
                        color="success"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  <Grid container spacing={3}>
                    {/* Top Words */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t('topWords', 'Most Frequent Words')}
                      </Typography>
                      {lexicalStats.topWords && lexicalStats.topWords.length > 0 ? (
                        <List dense>
                          {lexicalStats.topWords.map((word, index) => (
                            <ListItem
                              key={word.id}
                              disablePadding
                              sx={{
                                borderLeft: 3,
                                borderColor: COLORS[index % COLORS.length],
                                mb: 0.5,
                                bgcolor: 'background.default',
                                borderRadius: 1,
                                px: 1,
                                py: 0.5,
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                      {word.surfaceForm}
                                      {word.lemma && word.lemma !== word.surfaceForm && (
                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                          ({word.lemma})
                                        </Typography>
                                      )}
                                    </Typography>
                                    <Chip
                                      label={word.totalOccurrences}
                                      size="small"
                                      color="primary"
                                      sx={{ fontWeight: 'bold', minWidth: 36 }}
                                    />
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Alert severity="info">
                          {t('noWordData', 'No word data yet — click Refresh from Transcripts to populate')}
                        </Alert>
                      )}
                    </Grid>

                    {/* Low Exposure Words */}
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t('lowExposureWords', 'Low Exposure Words (≤3 seen)')}
                      </Typography>
                      {lexicalStats.lowExposureWords && lexicalStats.lowExposureWords.length > 0 ? (
                        <List dense>
                          {lexicalStats.lowExposureWords.map((word) => (
                            <ListItem
                              key={word.id}
                              disablePadding
                              sx={{
                                borderLeft: 3,
                                borderColor: '#ffc658',
                                mb: 0.5,
                                bgcolor: 'background.default',
                                borderRadius: 1,
                                px: 1,
                                py: 0.5,
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ flex: 1 }}>
                                      {word.surfaceForm}
                                      {word.lemma && word.lemma !== word.surfaceForm && (
                                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                          ({word.lemma})
                                        </Typography>
                                      )}
                                    </Typography>
                                    <Chip
                                      label={`${word.totalOccurrences}×`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ minWidth: 36 }}
                                    />
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Alert severity="info">
                          {t('noLowExposureData', 'No low-exposure words found')}
                        </Alert>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Alert severity="info">
                  {t('noWordData', 'No word data yet — click Refresh from Transcripts to populate')}
                </Alert>
              )}
            </Paper>
          </Grid>

          {/* Exposure Distribution Chart */}
          {exposureChartData.length > 0 && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="h6">
                    {t('exposureDistribution', 'Word Exposure Distribution')}
                  </Typography>
                  <ToggleButtonGroup
                    value={exposureView}
                    exclusive
                    onChange={(_e, v) => { if (v) setExposureView(v); }}
                    size="small"
                  >
                    <ToggleButton value="pct">%</ToggleButton>
                    <ToggleButton value="count">#</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {exposureView === 'pct'
                    ? t('exposureDistributionDescPct', 'Share of unique words in each exposure tier')
                    : t('exposureDistributionDescCount', 'Unique word count per tier (log scale)')
                  }
                </Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={exposureChartData} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 13 }} />
                    <YAxis
                      scale={exposureView === 'count' ? 'log' : 'linear'}
                      domain={exposureView === 'count' ? [1, 'auto'] : [0, 100]}
                      tickFormatter={exposureView === 'pct' ? (v: number) => `${v}%` : (v: number) => v.toLocaleString()}
                      tick={{ fontSize: 11 }}
                      allowDataOverflow
                    />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const d = payload[0].payload;
                          return (
                            <Paper sx={{ p: 1.5 }}>
                              <Typography variant="body2" fontWeight="bold">{d.name}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {t('wordsCountPct', '{{count}} words ({{pct}}%)', { count: d.count, pct: d.pct })}
                              </Typography>
                            </Paper>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey={exposureView === 'pct' ? 'pct' : 'count'} radius={[4, 4, 0, 0]}>
                      {exposureChartData.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={BUCKET_COLORS[index % BUCKET_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}

          {/* Vocabulary Exposure Breakdown */}
          {vocabChartData.length > 0 && lexicalStats?.vocabDistribution && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <MenuBookIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">
                    {t('vocabExposureBreakdown', 'Vocabulary Exposure Breakdown')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('vocabExposureBreakdownDesc', 'Where your saved vocabulary words fall in the exposure tiers')}
                </Typography>
                {(() => {
                  const v = lexicalStats.vocabDistribution;
                  const total = v.single + v.low + v.building + v.strong + v.phrases + v.notMapped;
                  const matched = v.single + v.low + v.building + v.strong;
                  return (
                    <>
                      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                        <Chip label={t('vocabTotal', '{{count}} total vocab words', { count: total })} size="small" variant="outlined" />
                        {v.phrases > 0 && (
                          <Chip label={t('vocabPhrases', '{{count}} phrases', { count: v.phrases })} size="small" variant="outlined" color="secondary" />
                        )}
                        {v.notMapped > 0 && (
                          <Chip label={t('vocabNotMapped', '{{count}} not yet seen', { count: v.notMapped })} size="small" variant="outlined" />
                        )}
                        <Chip label={t('vocabMapped', '{{count}} in exposure tiers', { count: matched })} size="small" variant="outlined" color="primary" />
                      </Stack>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={vocabChartData}
                            cx="50%"
                            cy="45%"
                            innerRadius={60}
                            outerRadius={95}
                            dataKey="value"
                            nameKey="name"
                          >
                            {vocabChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const d = payload[0].payload;
                                const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
                                return (
                                  <Paper sx={{ p: 1.5 }}>
                                    <Typography variant="body2" fontWeight="bold">{d.name}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {t('wordsCountPct', '{{count}} words ({{pct}}%)', { count: d.value, pct })}
                                    </Typography>
                                  </Paper>
                                );
                              }
                              return null;
                            }}
                          />
                          <Legend
                            layout="horizontal"
                            verticalAlign="bottom"
                            align="center"
                            formatter={(value, entry) => {
                              const d = entry.payload as { value: number };
                              const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
                              return `${value} (${pct}%)`;
                            }}
                            wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </>
                  );
                })()}
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Learning Analytics Section */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          {t('learningAnalytics', 'Learning Analytics')}
        </Typography>

        <Grid container spacing={3}>
          {/* Top Reviewed Segments */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <RepeatIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  {t('topReviewedSegments', 'Most Reviewed Segments')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('topReviewedDescription', 'Segments you\'ve replayed the most often')}
              </Typography>

              {topReviewedLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : topReviewedData && topReviewedData.length > 0 ? (
                <List>
                  {topReviewedData.map((segment, index) => (
                    <ListItem
                      key={segment.segmentId}
                      disablePadding
                      sx={{
                        borderLeft: 3,
                        borderColor: COLORS[index % COLORS.length],
                        mb: 1,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                      }}
                    >
                      <ListItemButton
                        onClick={() => navigate(getSegmentPlayerPath(segment))}
                      >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {segment.segmentText.substring(0, 100)}
                              {segment.segmentText.length > 100 ? '...' : ''}
                            </Typography>
                            <Chip
                              label={`${segment.playCount}×`}
                              size="small"
                              color="primary"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary">
                              {segment.episodeTitle}
                            </Typography>
                            {segment.vocabCount > 0 && (
                              <Chip
                                label={t('vocabWords', '{{count}} vocab words', { count: segment.vocabCount })}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            <Chip label={segment.language} size="small" variant="outlined" />
                            {segment.contentType === 'youtube' && (
                              <Chip
                                label={t('youtube', 'YouTube')}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                      />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Alert severity="info">
                  {t('noReviewData', 'Start reviewing segments to see statistics here')}
                </Alert>
              )}
            </Paper>
          </Grid>

          {/* Vocabulary Density */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MenuBookIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  {t('vocabularyDensity', 'Vocabulary Density')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('vocabularyDensityDescription', 'Episodes with the most vocabulary words')}
              </Typography>

              {vocabDensityLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : vocabDensityData && vocabDensityData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={vocabDensityData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="title" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={100} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <Paper sx={{ p: 1.5 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {data.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {t('vocabWords', '{{count}} vocab words', { count: data.vocabCount })}
                              </Typography>
                              <Chip label={data.language} size="small" sx={{ mt: 0.5 }} />
                            </Paper>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="vocabCount" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Alert severity="info">
                  {t('noVocabData', 'Add vocabulary words to episodes to see statistics here')}
                </Alert>
              )}
            </Paper>
          </Grid>

          {/* YouTube Activity */}
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <OndemandVideoIcon sx={{ mr: 1, color: 'error.main' }} />
                <Typography variant="h6">
                  {t('youtubeActivity', 'YouTube Activity')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('youtubeActivityDescription', 'Your engagement with YouTube video content')}
              </Typography>

              {ytEngagementLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : ytEngagement && (ytEngagement.total_videos > 0 || ytEngagement.total_segments_created > 0 || ytEngagement.total_vocab_saved > 0) ? (
                <>
                  <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
                    <Chip
                      icon={<OndemandVideoIcon />}
                      label={t('youtubeVideos', '{{count}} YouTube videos', { count: ytEngagement.total_videos ?? 0 })}
                      color="error"
                      variant="outlined"
                    />
                    <Chip
                      label={t('youtubeSegments', '{{count}} segments created', { count: ytEngagement.total_segments_created ?? 0 })}
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={t('youtubeVocab', '{{count}} vocab saved', { count: ytEngagement.total_vocab_saved ?? 0 })}
                      color="success"
                      variant="outlined"
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {t('youtubeListeningNote', 'YouTube listening time is included in the Listening Time chart above')}
                  </Typography>
                </>
              ) : (
                <Alert severity="info">
                  {t('noYouTubeData', 'Start studying YouTube videos to see your activity here')}
                </Alert>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
